// src/components/CMRLoadModal.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "../axiosConfig";
import "../styles/CMRLoadModal.css";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";

// ✅ absolutize backend/object-storage photo paths
const API_ORIGIN =
  (axios.defaults?.baseURL || "").replace(/\/api\/?$/, "") || window.location.origin;

const toAbsolutePhotoUrl = (p) => {
  if (!p || typeof p !== "string") return null;
  if (/^https?:\/\//i.test(p)) return p;

  const clean = p.replace(/\/uploads\/uploads\//g, "/uploads/");

  if (clean.startsWith("/uploads/")) return `${API_ORIGIN}${clean}`;
  if (clean.startsWith("uploads/")) return `${API_ORIGIN}/${clean}`;

  return `${API_ORIGIN}/uploads/${clean.replace(/^\/+/, "")}`;
};

const normalizeDocPhotos = (arr) => {
  const safeArr = Array.isArray(arr) ? arr : [];

  return safeArr
    .map((item) =>
      typeof item === "string"
        ? {
            key: item,
            url: toAbsolutePhotoUrl(item) || item,
            sizeBytes: 0,
          }
        : {
            key: item?.key || item?.url || "",
            url: toAbsolutePhotoUrl(item?.url || item?.key || "") || "",
            sizeBytes: Number(item?.sizeBytes) || 0,
          }
    )
    .filter((photo) => photo?.url);
};

const MAX_TOTAL_UPLOAD_MB = 10;
const MAX_TOTAL_UPLOAD_BYTES = MAX_TOTAL_UPLOAD_MB * 1024 * 1024;

const WEB_PHOTO_MAX_DIMENSION = 1600;
const WEB_PHOTO_JPEG_QUALITY = 0.72;

const getDirectPhotoApiOrigin = () => {
  const hostname = String(
    window.location.hostname || ""
  ).toLowerCase();

  if (
    hostname ===
    "app.loadevo.com"
  ) {
    return "https://api.loadevo.com";
  }

  if (
    hostname ===
    "test-app.loadevo.com"
  ) {
    return "https://test-api.loadevo.com";
  }

  const configuredBaseUrl =
    String(
      axios.defaults?.baseURL ||
      ""
    ).trim();

  if (
    /^https?:\/\//i.test(
      configuredBaseUrl
    )
  ) {
    return configuredBaseUrl.replace(
      /\/api\/?$/,
      ""
    );
  }

  return "";
};

const DIRECT_PHOTO_API_ORIGIN =
  getDirectPhotoApiOrigin();

const normalizeStoredAuthToken = (
  value
) => {
  if (
    !value
  ) {
    return "";
  }

  let rawValue =
    value;

  try {
    const parsed =
      JSON.parse(
        String(
          value
        )
      );

    if (
      parsed &&
      typeof parsed ===
        "object"
    ) {
      rawValue =
        parsed.token ||
        parsed.accessToken ||
        parsed.authToken ||
        parsed.jwt ||
        "";
    }
  } catch {}

  return String(
    rawValue ||
    ""
  )
    .trim()
    .replace(
      /^Bearer\s+/i,
      ""
    )
    .replace(
      /^"|"$/g,
      ""
    );
};

const getUploadAuthToken = () => {
  const candidates = [
    localStorage.getItem(
      "token"
    ),
    sessionStorage.getItem(
      "token"
    ),
    localStorage.getItem(
      "authToken"
    ),
    sessionStorage.getItem(
      "authToken"
    ),
    localStorage.getItem(
      "accessToken"
    ),
    sessionStorage.getItem(
      "accessToken"
    ),
    localStorage.getItem(
      "auth_user"
    ),
    sessionStorage.getItem(
      "auth_user"
    ),
  ];

  for (
    const candidate of
    candidates
  ) {
    const token =
      normalizeStoredAuthToken(
        candidate
      );

    if (
      token
    ) {
      return token;
    }
  }

  return "";
};

const compressPhotoForUpload = (
  file
) => {
  return new Promise(
    (
      resolve
    ) => {
      if (
        !file ||
        !String(
          file.type ||
          ""
        ).startsWith(
          "image/"
        )
      ) {
        resolve(
          file
        );
        return;
      }

      const objectUrl =
        URL.createObjectURL(
          file
        );

      const image =
        new Image();

      image.onload =
        () => {
          try {
            const originalWidth =
              Number(
                image.naturalWidth
              ) ||
              Number(
                image.width
              ) ||
              0;

            const originalHeight =
              Number(
                image.naturalHeight
              ) ||
              Number(
                image.height
              ) ||
              0;

            if (
              !originalWidth ||
              !originalHeight
            ) {
              URL.revokeObjectURL(
                objectUrl
              );

              resolve(
                file
              );

              return;
            }

            const longestSide =
              Math.max(
                originalWidth,
                originalHeight
              );

            const scale =
              longestSide >
              WEB_PHOTO_MAX_DIMENSION
                ? WEB_PHOTO_MAX_DIMENSION /
                  longestSide
                : 1;

            const targetWidth =
              Math.max(
                1,
                Math.round(
                  originalWidth *
                    scale
                )
              );

            const targetHeight =
              Math.max(
                1,
                Math.round(
                  originalHeight *
                    scale
                )
              );

            const canvas =
              document.createElement(
                "canvas"
              );

            canvas.width =
              targetWidth;

            canvas.height =
              targetHeight;

            const context =
              canvas.getContext(
                "2d"
              );

            if (
              !context
            ) {
              URL.revokeObjectURL(
                objectUrl
              );

              resolve(
                file
              );

              return;
            }

            context.fillStyle =
              "#ffffff";

            context.fillRect(
              0,
              0,
              targetWidth,
              targetHeight
            );

            context.drawImage(
              image,
              0,
              0,
              targetWidth,
              targetHeight
            );

            canvas.toBlob(
              (
                blob
              ) => {
                URL.revokeObjectURL(
                  objectUrl
                );

                if (
                  !blob
                ) {
                  resolve(
                    file
                  );

                  return;
                }

                const baseName =
                  String(
                    file.name ||
                    "photo"
                  ).replace(
                    /\.[^/.]+$/,
                    ""
                  );

                const compressedFile =
                  new File(
                    [
                      blob,
                    ],
                    `${baseName}.jpg`,
                    {
                      type:
                        "image/jpeg",

                      lastModified:
                        file.lastModified ||
                        Date.now(),
                    }
                  );

                if (
                  compressedFile.size >=
                  file.size
                ) {
                  resolve(
                    file
                  );

                  return;
                }

                resolve(
                  compressedFile
                );
              },
              "image/jpeg",
              WEB_PHOTO_JPEG_QUALITY
            );
          } catch (
            error
          ) {
            URL.revokeObjectURL(
              objectUrl
            );

            console.warn(
              "[CMRLoadModal] Browser compression failed. Using original file.",
              error
            );

            resolve(
              file
            );
          }
        };

      image.onerror =
        () => {
          URL.revokeObjectURL(
            objectUrl
          );

          resolve(
            file
          );
        };

      image.src =
        objectUrl;
    }
  );
};

const formatBytesMB = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const withCacheBust = (url) => {
  if (!url) return "";
  return url;
};

export default function CMRLoadModal({
  isOpen,
  onClose,
  pickupDeliveryId,
  title,
  readOnly = false,
  footerText = null,
}) {
  const { t } = useTranslation("cmr_load_modal");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [docPhotos, setDocPhotos] = useState([]);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);

  // ✅ Amplify / Lightbox viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerSrc, setViewerSrc] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);

  const canRender = isOpen && pickupDeliveryId;

  const previewFiles = useMemo(() => {
    if (readOnly) return [];
    return (files || []).map((f, idx) => ({
      idx,
      name: f.name,
      url: URL.createObjectURL(f),
    }));
  }, [files, readOnly]);

  const fetchDocPhotos = async () => {
    if (!pickupDeliveryId) return;

    setLoading(true);
    try {
      const res = await axios.get(`/pickupdeliveries/${pickupDeliveryId}/docPhotos`);
      const arr = Array.isArray(res.data?.docPhotos) ? res.data.docPhotos : [];
      const normalized = normalizeDocPhotos(arr);

      setDocPhotos(normalized);
      setUploadedBytes(
        Number(res.data?.usedBytes) ||
          normalized.reduce((sum, photo) => sum + (Number(photo?.sizeBytes) || 0), 0)
      );
    } catch (err) {
      console.error("CMRLoadModal fetchDocPhotos failed:", err);
      setDocPhotos([]);
      setUploadedBytes(0);
    } finally {
      setLoading(false);
    }
  };

  const openViewer = (url, idx = 0) => {
    const normalized = toAbsolutePhotoUrl(url) || url;
    setViewerSrc(normalized);
    setViewerIndex(idx);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setViewerSrc(null);
  };

  const stepViewer = (dir) => {
    if (!docPhotos?.length) return;
    const next = (viewerIndex + dir + docPhotos.length) % docPhotos.length;
    const normalized = toAbsolutePhotoUrl(docPhotos[next]?.url) || null;
    setViewerIndex(next);
    setViewerSrc(normalized);
  };

  const downloadViewer = async () => {
    try {
      if (!viewerSrc) return;

      const extFromUrl = (() => {
        try {
          const u = new URL(viewerSrc, window.location.href);
          const clean = u.pathname.split("?")[0];
          const last = clean.split(".").pop();
          if (last && last.length <= 5) return last.toLowerCase();
        } catch {}
        return "jpg";
      })();

      const res = await fetch(viewerSrc, { mode: "cors" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `cmr-${pickupDeliveryId}-${viewerIndex + 1}.${extFromUrl}`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      console.error("Download failed:", e);
      alert(t("alerts.downloadFailed", { defaultValue: "❌ Failed to download image." }));
    }
  };

  const selectedTotalBytes = useMemo(() => {
    return (files || []).reduce((sum, f) => sum + (Number(f?.size) || 0), 0);
  }, [files]);

  const totalUsedBytes = uploadedBytes + selectedTotalBytes;
  const remainingBytes = Math.max(0, MAX_TOTAL_UPLOAD_BYTES - uploadedBytes);
  const willExceedTotalLimit = totalUsedBytes > MAX_TOTAL_UPLOAD_BYTES;

  const removePreviewAt = (idxToRemove) => {
    setFiles((prev) => {
      const next = (prev || []).filter((_, i) => i !== idxToRemove);

      const totalBytes = next.reduce((sum, f) => sum + (Number(f?.size) || 0), 0);

      if (totalBytes <= MAX_TOTAL_UPLOAD_BYTES) {
        setFileError("");
      }

      return next;
    });
  };

  const handleFileChange = async (e) => {
    const selectedFiles =
      Array.from(
        e.target.files ||
        []
      );

    if (
      !selectedFiles.length
    ) {
      setFiles([]);
      setFileError("");
      return;
    }

    setCompressing(true);
    setFileError("");

    try {
      const compressedFiles =
        await Promise.all(
          selectedFiles.map(
            (
              file
            ) =>
              compressPhotoForUpload(
                file
              )
          )
        );

      const totalSelectedBytes =
        compressedFiles.reduce(
          (
            sum,
            file
          ) =>
            sum +
            (
              Number(
                file?.size
              ) ||
              0
            ),
          0
        );

      const nextTotalUsedBytes =
        uploadedBytes +
        totalSelectedBytes;

      if (
        nextTotalUsedBytes >
        MAX_TOTAL_UPLOAD_BYTES
      ) {
        setFiles([]);

        setFileError(
          t(
            "alerts.totalSizeExceeded",
            {
              maxMB:
                MAX_TOTAL_UPLOAD_MB,

              selectedMB:
                formatBytesMB(
                  totalSelectedBytes
                ),

              usedMB:
                formatBytesMB(
                  uploadedBytes
                ),

              totalMB:
                formatBytesMB(
                  nextTotalUsedBytes
                ),

              defaultValue:
                `⚠️ Total limit exceeded. Already uploaded: ${formatBytesMB(uploadedBytes)}. ` +
                `Selected now: ${formatBytesMB(totalSelectedBytes)}. ` +
                `Total would be: ${formatBytesMB(nextTotalUsedBytes)} / ${MAX_TOTAL_UPLOAD_MB} MB.`,
            }
          )
        );

        if (
          fileInputRef.current
        ) {
          fileInputRef.current.value =
            "";
        }

        return;
      }

      console.log(
        "[CMRLoadModal] photo compression",
        selectedFiles.map(
          (
            original,
            index
          ) => ({
            name:
              original.name,

            originalBytes:
              original.size,

            compressedBytes:
              compressedFiles[
                index
              ]?.size ||
              0,

            reduction:
              original.size
                ? `${Math.round(
                    (
                      1 -
                      (
                        compressedFiles[
                          index
                        ]?.size ||
                        original.size
                      ) /
                        original.size
                    ) *
                      100
                  )}%`
                : "0%",
          })
        )
      );

      setFiles(
        compressedFiles
      );
    } catch (
      error
    ) {
      console.error(
        "[CMRLoadModal] compression failed:",
        error
      );

      setFiles(
        selectedFiles
      );
    } finally {
      setCompressing(
        false
      );
    }
  };

  useEffect(() => {
    return () => {
      previewFiles.forEach((p) => {
        try {
          URL.revokeObjectURL(p.url);
        } catch {}
      });
    };
  }, [previewFiles]);

  useEffect(() => {
    if (!canRender) return;
    fetchDocPhotos();
  }, [canRender, pickupDeliveryId]);

  useEffect(() => {
    if (!viewerOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeViewer();
      if (e.key === "ArrowLeft") stepViewer(-1);
      if (e.key === "ArrowRight") stepViewer(1);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [viewerOpen, viewerIndex, docPhotos]);

  const uploadNow = async () => {
    if (readOnly) return;
    if (!files?.length) return;

    const currentSelectedBytes = (files || []).reduce(
      (sum, f) => sum + (Number(f?.size) || 0),
      0
    );

    const nextTotalUsedBytes = uploadedBytes + currentSelectedBytes;

    if (nextTotalUsedBytes > MAX_TOTAL_UPLOAD_BYTES) {
      const msg = t("alerts.totalSizeExceeded", {
        maxMB: MAX_TOTAL_UPLOAD_MB,
        selectedMB: formatBytesMB(currentSelectedBytes),
        usedMB: formatBytesMB(uploadedBytes),
        totalMB: formatBytesMB(nextTotalUsedBytes),
        defaultValue:
          `⚠️ Total limit exceeded. Already uploaded: ${formatBytesMB(uploadedBytes)}. ` +
          `Selected now: ${formatBytesMB(currentSelectedBytes)}. ` +
          `Total would be: ${formatBytesMB(nextTotalUsedBytes)} / ${MAX_TOTAL_UPLOAD_MB} MB.`,
      });

      setFileError(msg);
      alert(msg);
      return;
    }

    const fd = new FormData();
    (files || []).forEach((f) => fd.append("docPhotos", f));

    setSaving(true);
    try {
      const uploadPath =
        `/pickupdeliveries/${pickupDeliveryId}/upload/docPhotos`;

      const uploadOrigin =
        DIRECT_PHOTO_API_ORIGIN ||
        API_ORIGIN;

      const uploadUrl =
        `${uploadOrigin}${uploadPath}`;

      const uploadToken =
        getUploadAuthToken();

      if (
        !uploadToken
      ) {
        throw new Error(
          "Authentication token not found."
        );
      }

      const formFiles =
        fd.getAll(
          "docPhotos"
        );

      console.log(
        "[CMRLoadModal] upload payload",
        {
          uploadUrl,

          fileCount:
            formFiles.length,

          files:
            formFiles.map(
              (
                file
              ) => ({
                name:
                  file?.name ||
                  null,

                type:
                  file?.type ||
                  null,

                size:
                  file?.size ||
                  0,

                isFile:
                  file instanceof
                  File,

                isBlob:
                  file instanceof
                  Blob,
              })
            ),
        }
      );

      if (
        !formFiles.length
      ) {
        throw new Error(
          "No files found in FormData before upload."
        );
      }

      const uploadResponse =
        await fetch(
          uploadUrl,
          {
            method:
              "POST",

            headers: {
              Authorization:
                `Bearer ${uploadToken}`,
            },

            body:
              fd,
          }
        );

      const uploadPayload =
        await uploadResponse
          .json()
          .catch(
            () =>
              null
          );

      if (
        !uploadResponse.ok
      ) {
        throw new Error(
          uploadPayload?.error ||
          uploadPayload?.message ||
          `Photo upload failed (${uploadResponse.status}).`
        );
      }

      setFiles([]);
      setFileError("");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await fetchDocPhotos();
    } catch (e) {
      console.error(
        "CMRLoadModal upload failed:",
        e
      );

      const serverMsg =
        e?.response?.data?.error ||
        e?.message ||
        t(
          "alerts.uploadFailed",
          {
            defaultValue:
              "❌ Failed to upload photos.",
          }
        );

      setFileError(
        serverMsg
      );

      alert(
        serverMsg
      );
    } finally {
      setSaving(false);
    }
  };

  if (!canRender) return null;

  const computedTitle = (title ?? "").trim() || t("title", { defaultValue: "Photos" });

  const deletePhoto = async (photoToDelete) => {
    if (readOnly) return;

    const prev = docPhotos;
    setDocPhotos((cur) => (cur || []).filter((x) => x.key !== photoToDelete.key));

    if (
      viewerOpen &&
      (viewerSrc?.includes(photoToDelete.url) || viewerSrc === photoToDelete.url)
    ) {
      closeViewer();
    }

    try {
      const { data } = await axios.delete(
        `/pickupdeliveries/${pickupDeliveryId}/docPhotos`,
        { data: { src: photoToDelete.key } }
      );

      const updated = Array.isArray(data?.docPhotos) ? data.docPhotos : null;

      if (updated) {
        setDocPhotos(normalizeDocPhotos(updated));
      } else {
        await fetchDocPhotos();
      }
    } catch (e) {
      console.error("CMRLoadModal delete photo failed:", e);
      setDocPhotos(prev);
      alert(t("alerts.deleteFailed", { defaultValue: "❌ Failed to delete photo." }));
    }
  };

  return (
    <div className="cmrload-backdrop" onMouseDown={onClose}>
      <div className="cmrload-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="cmrload-top-card">
          <div className="cmrload-top-card-label"></div>

          <div className="cmrload-top-card-title">{computedTitle}</div>

          {!readOnly && (
            <div className="cmrload-top-card-subtitle">
              {t("subtitle", { defaultValue: "Manage CMR photos for this point" })}
            </div>
          )}
        </div>

        <div className="cmrload-body">
          <div className="cmrload-section">
            <div
              className={`cmrload-card ${
                !loading && !docPhotos.length ? "cmrload-card-compact" : ""
              }`}
            >
              <div className="cmrload-section-title">
                {t("sections.existingTitle", { defaultValue: "Existing photos" })}
              </div>

              <div className="cmrload-card-inner">
                {loading ? (
                  <div className="cmrload-muted">
                    {t("states.loading", { defaultValue: "Loading…" })}
                  </div>
                ) : docPhotos.length ? (
                  <div className="cmrload-grid">
                    {docPhotos.map((photo, idx) => (
                      <div
                        className="cmrload-thumb-wrap"
                        key={`${photo.key || photo.url}-${idx}`}
                      >
                        <button
                          type="button"
                          className="cmrload-thumb"
                          onClick={() => openViewer(photo.url, idx)}
                          title={t("tooltips.enlarge", {
                            defaultValue: "Click to enlarge",
                          })}
                        >
                          <img
                            src={photo.url}
                            alt={t("aria.thumbAlt", {
                              idx: idx + 1,
                              defaultValue: `Photo ${idx + 1}`,
                            })}
                          />
                        </button>

                        {!readOnly && (
                          <button
                            type="button"
                            className="cmrload-thumb-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              deletePhoto(photo);
                            }}
                            aria-label={t("aria.delete", {
                              defaultValue: "Delete photo",
                            })}
                            title={t("aria.delete", { defaultValue: "Delete" })}
                            disabled={saving}
                          >
                            <Trash2 size={16} strokeWidth={2.1} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="cmrload-muted">
                    {t("states.empty", { defaultValue: "No photos yet." })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {!readOnly ? (
            <div className="cmrload-section">
              <div className="cmrload-card">
                <div className="cmrload-section-title">
                  {t("sections.addTitle", { defaultValue: "Add new photos" })}
                </div>

                <div className="cmrload-card-inner">
                  <div className="cmrload-upload-box">
                    <input
                      ref={fileInputRef}
                      className="cmrload-file"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileChange}
                      disabled={saving || compressing}
                      aria-label={t("aria.fileInput", {
                        defaultValue: "Select photos",
                      })}
                    />
                  </div>

                  <div className="cmrload-upload-meta">
                    {t("labels.uploadedSize", { defaultValue: "Already uploaded" })}:{" "}
                    <strong>{formatBytesMB(uploadedBytes)}</strong>
                    {" • "}
                    {t("labels.selectedSize", { defaultValue: "Selected now" })}:{" "}
                    <strong>{formatBytesMB(selectedTotalBytes)}</strong>
                    {" • "}
                    {t("labels.totalUsedSize", { defaultValue: "Total used" })}:{" "}
                    <strong>{formatBytesMB(totalUsedBytes)}</strong> /{" "}
                    {MAX_TOTAL_UPLOAD_MB} MB
                    {" • "}
                    {t("labels.remainingSize", { defaultValue: "Remaining" })}:{" "}
                    <strong>
                      {formatBytesMB(
                        Math.max(0, MAX_TOTAL_UPLOAD_BYTES - totalUsedBytes)
                      )}
                    </strong>
                  </div>

                  {!!fileError && (
                    <div className="cmrload-error-banner">{fileError}</div>
                  )}

                  {previewFiles.length > 0 && (
                    <div className="cmrload-grid cmrload-grid-preview">
                      {previewFiles.map((p) => (
                        <div className="cmrload-thumb-wrap" key={`${p.name}-${p.idx}`}>
                          <div className="cmrload-thumb cmrload-thumb-static">
                            <img
                              src={p.url}
                              alt={t("aria.previewAlt", {
                                name: p.name,
                                defaultValue: `Preview ${p.name}`,
                              })}
                            />
                          </div>

                          <button
                            type="button"
                            className="cmrload-thumb-delete"
                            onClick={() => removePreviewAt(p.idx)}
                            aria-label={t("aria.removePreview", {
                              defaultValue: "Remove photo",
                            })}
                            title={t("aria.removePreview", { defaultValue: "Remove" })}
                            disabled={saving}
                          >
                            <Trash2 size={16} strokeWidth={2.1} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="cmrload-actions">
                    <button
                      className="cmrload-btn secondary"
                      onClick={onClose}
                      disabled={saving}
                    >
                      {t("buttons.close", { defaultValue: "Close" })}
                    </button>

                    <button
                      className="cmrload-btn"
                      onClick={uploadNow}
                      disabled={
                        saving ||
                        compressing ||
                        !files?.length ||
                        willExceedTotalLimit
                      }
                    >
                      {compressing
                        ? t("buttons.compressing", {
                            defaultValue: "Compressing…",
                          })
                        : saving
                        ? t("buttons.uploading", {
                            defaultValue: "Uploading…",
                          })
                        : t("buttons.upload", {
                            defaultValue: "Upload",
                          })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="cmrload-section">
              <div className="cmrload-card">
                <div className="cmrload-actions cmrload-actions-single">
                  <button
                    className="cmrload-btn secondary"
                    onClick={onClose}
                    disabled={saving}
                  >
                    {footerText || t("buttons.close", { defaultValue: "Close" })}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {viewerOpen && (
          <div className="cmrload-viewer-backdrop" onMouseDown={closeViewer}>
            <div className="cmrload-viewer" onMouseDown={(e) => e.stopPropagation()}>
              <div className="cmrload-viewer-tools">
                <button
                  type="button"
                  className="cmrload-viewer-download"
                  onClick={downloadViewer}
                  aria-label={t("aria.download", { defaultValue: "Download" })}
                  title={t("aria.download", { defaultValue: "Download" })}
                >
                  ⬇
                </button>

                <button
                  type="button"
                  className="cmrload-viewer-close"
                  onClick={closeViewer}
                  aria-label={t("aria.close", { defaultValue: "Close" })}
                  title={t("aria.close", { defaultValue: "Close" })}
                >
                  ✕
                </button>
              </div>

              {docPhotos.length > 1 && (
                <>
                  <button
                    type="button"
                    className="cmrload-viewer-nav left"
                    onClick={() => stepViewer(-1)}
                    aria-label={t("aria.prev", { defaultValue: "Previous" })}
                    title={t("aria.prev", { defaultValue: "Previous" })}
                  >
                    ‹
                  </button>

                  <button
                    type="button"
                    className="cmrload-viewer-nav right"
                    onClick={() => stepViewer(1)}
                    aria-label={t("aria.next", { defaultValue: "Next" })}
                    title={t("aria.next", { defaultValue: "Next" })}
                  >
                    ›
                  </button>
                </>
              )}

              <img
                className="cmrload-viewer-img"
                src={viewerSrc}
                alt={t("aria.viewerAlt", { defaultValue: "Preview" })}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}